import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Activity, PauseCircle, PlayCircle } from 'lucide-react';
import { getOpsStatus } from '../../features/dataflow/api';
import type { OpsStatusPipeline } from '../../features/dataflow/types';
import { formatRelativeTime } from './helpers';
import {
  groupObserveFeedChips,
  observeFeedChipTitle,
  shortPipeId,
} from './groupObserveFeedChips';

export default function IngestPipelineStrip() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['api-observe', 'ingest-pipelines', 'ops-status'],
    queryFn: getOpsStatus,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const pipelines = (data?.api_pipelines?.pipelines || []) as OpsStatusPipeline[];
  const enabled = data?.api_pipelines?.enabled ?? pipelines.filter((p) => p.status !== 'disabled').length;
  const disabled = data?.api_pipelines?.disabled ?? Math.max(0, (data?.api_pipelines?.total || 0) - enabled);
  const groups = groupObserveFeedChips(pipelines);
  const errorMessage = (error as Error)?.message || '';
  const timedOut = /timeout|timed out|504/i.test(errorMessage);

  return (
    <section className="mb-6 rounded-xl border bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-indigo-600" />
          <h2 className="text-sm font-semibold text-gray-900">API Ingest Feeds</h2>
          <p className="text-[11px] text-gray-500">
            Same store as Data Flow ops/status (api_ingest_pipelines + api_ingest_history) — not Timescale MCP telemetry
          </p>
        </div>
        <div className="flex items-center gap-4 text-xs text-gray-600">
          <span><strong className="text-gray-900">{data?.api_pipelines?.total ?? pipelines.length}</strong> scheduled</span>
          <span className="text-emerald-700"><strong>{enabled}</strong> enabled</span>
          <span className="text-gray-500"><strong>{disabled}</strong> disabled</span>
          {pipelines.length > 0 && groups.length !== pipelines.length && (
            <span className="text-indigo-700" title="Near-duplicate pipes sharing a provider family are grouped; scheduled/enabled stay the raw census">
              <strong>{groups.length}</strong> lanes
            </span>
          )}
        </div>
      </div>

      {isLoading && <p className="text-sm text-gray-500">Loading Data Flow ingest census…</p>}
      {error && (
        <p className="text-sm text-red-600">
          {timedOut
            ? 'Data Flow ingest census timed out — this view, not “no ingest exists”.'
            : `Ingest census unavailable: ${errorMessage || 'unknown error'}`}
        </p>
      )}

      {!isLoading && !error && pipelines.length === 0 && (
        <p className="text-sm text-gray-500">
          No scheduled pipeline objects in Data Flow ops/status. MCP tool telemetry below is a different store — this empty is this view, not “no ingest exists”.
        </p>
      )}

      {!isLoading && !error && groups.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {groups.map((group) => {
            const pipe = group.representative;
            const live = pipe.status !== 'disabled' && pipe.health !== 'error';
            const d = group.distinguisher;
            const distinguisherParts = [
              d.endpoint_id,
              shortPipeId(d.pipeline_id),
              d.last_success_at ? formatRelativeTime(d.last_success_at) : 'never succeeded',
            ].filter(Boolean);
            return (
              <span
                key={group.familyKey}
                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${
                  pipe.health === 'error'
                    ? 'border-red-200 bg-red-50 text-red-800'
                    : live
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                      : 'border-gray-200 bg-gray-50 text-gray-600'
                }`}
                title={observeFeedChipTitle(group)}
              >
                {live ? <PlayCircle className="w-3 h-3" /> : <PauseCircle className="w-3 h-3" />}
                <span className="font-medium truncate max-w-[160px]">{group.label}</span>
                <span className="opacity-70">{pipe.health || pipe.status}</span>
                <span className="opacity-60 truncate max-w-[220px]">{distinguisherParts.join(' · ')}</span>
                {d.member_count > 1 && (
                  <span className="opacity-70 whitespace-nowrap">
                    ×{d.member_count}
                    {d.healthy_count > 0 && d.healthy_count < d.member_count
                      ? ` (${d.healthy_count} healthy)`
                      : ''}
                  </span>
                )}
              </span>
            );
          })}
        </div>
      )}
    </section>
  );
}
